import { RowMode } from "../../generated/core-api";
import { parseOnboardingDate } from "../../onboarding/utils/onboardingDates";
import { parseCsv } from "./csvParser";
import {
  END_DATE_HEADER_LC,
  START_DATE_HEADER_LC,
  resolveDateColumnHeader,
} from "./dateColumns";
import { RESERVED_LC } from "./mappedRows";

/**
 * Normalise a CSV assignment-date cell to ISO `yyyy-MM-dd` for the commit wire.
 * Blank → undefined (omit). Invalid → leave the raw trimmed value so the Api
 * still rejects with a validation error rather than silently dropping the date.
 */
function wireDateFromCsvCell(raw: string, fieldName: string): string | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const parsed = parseOnboardingDate(trimmed, fieldName);
  if ("error" in parsed) return trimmed;
  return parsed.value ?? undefined;
}

/**
 * A single parsed import row, matching the API's `ImportRow` shape. Sent inline
 * in the `pay_component_import` workflow payload (the commit no longer uploads a
 * CSV — it posts structured rows to the agent-invocation endpoint).
 */
export interface ImportRow {
  companyCode: string;
  employeeCode: string;
  /** Component display name (the API's `componentName` wire field). */
  componentName: string;
  /**
   * Disambiguating company-component id (D4). Carried ONLY when the component
   * name is ambiguous (`isAmbiguous`) and the PS user has selected an instance;
   * absent otherwise (the server resolves a non-ambiguous name by name alone).
   * An ambiguous name posted without this id is rejected server-side as
   * `PAYCOMP_COMPONENT_AMBIGUOUS`.
   */
  companyComponentId?: string;
  columns: Record<string, string>;
  /**
   * ISO date (`YYYY-MM-DD`) from the mapped start-date column (reserved
   * `Start Date` by default). NZ spreadsheet forms are normalised via
   * {@link parseOnboardingDate} before commit. Omitted when blank.
   */
  startDate?: string;
  /**
   * ISO date (`YYYY-MM-DD`) from the mapped end-date column (reserved `End Date`
   * by default). Normalised like `startDate`. Christmas applies
   * `FinishedDate = endDate + 1 day` server-side (per #892), so the client does
   * no date math. Omitted entirely when the cell is blank.
   */
  endDate?: string;
  /**
   * LADR-038 bulk import row mode. Defaults to `RowMode.Create`; a per-row
   * `Row Mode` column value wins when present, else the wizard-level override.
   */
  rowMode: RowMode;
}

export interface BuildImportRowsOptions {
  companyCode: string;
  /** Header that identifies the component; falls back to the detected column. */
  componentColumn?: string | null;
  /**
   * Header that supplies per-row start dates; falls back to reserved `Start Date`.
   */
  startDateColumn?: string | null;
  /**
   * Header that supplies per-row end dates; falls back to reserved `End Date`.
   */
  endDateColumn?: string | null;
  /**
   * Wizard-level row mode applied to every row (D2). A per-row `Row Mode` CSV
   * column overrides it per row. Defaults to `RowMode.Create`.
   */
  rowMode?: RowMode | null;
  /**
   * Per-component-name ambiguity resolutions (D4). When a row's component name
   * resolves to an entry carrying a `companyComponentId`, that id is attached to
   * the wire row to disambiguate. Names absent from the map (or without a chosen
   * id) carry no id — an ambiguous+unresolved name is caught separately by
   * {@link findUnresolvedAmbiguousRows}.
   */
  componentResolutions?: ComponentResolutionMap;
}

/**
 * A single component name's ambiguity resolution at the wire boundary (D4).
 * `isAmbiguous` mirrors the discovery envelope; `companyComponentId` is the PS
 * user's chosen instance for an ambiguous name (absent until they pick one).
 */
export interface ComponentResolution {
  isAmbiguous: boolean;
  companyComponentId?: string;
}

/** Keyed by component (display) name → its ambiguity resolution. */
export type ComponentResolutionMap = Record<string, ComponentResolution>;

/**
 * A wire row whose component name is ambiguous but has no selected
 * `companyComponentId`. Surfaced as a blocking `PAYCOMP_COMPONENT_AMBIGUOUS`
 * validation error before the commit is posted (D4).
 */
export interface UnresolvedAmbiguousRow {
  /** 1-based source row number (matches the CSV data-row order). */
  rowIndex: number;
  employeeCode: string;
  componentName: string;
}

const EMPLOYEE_CODE_HEADER_LC = "employee code";
const ROW_MODE_HEADER_LC = "row mode";

/**
 * Parse a `Row Mode` cell into a {@link RowMode} (case-insensitive), or `null`
 * when the cell is blank or holds an unrecognised value — in which case the
 * caller falls back to the wizard-level default.
 */
function parseRowMode(raw: string | undefined): RowMode | null {
  const value = (raw ?? "").trim().toLowerCase();
  if (value === "create") return RowMode.Create;
  if (value === "update") return RowMode.Update;
  return null;
}

/**
 * Build the structured `ImportRow[]` the commit workflow expects from raw CSV
 * text. Mirrors the server's CSV parsing contract:
 *
 * - `Employee Code` → employee scope code (case-insensitive). LADR-038 imports
 *   are employee-scoped only — there is no EmployeeGroup scope.
 * - The component column (override or detected `Component Name` / `Deduction
 *   Name`) → component name.
 * - Start-date column (override or reserved `Start Date`) → per-row
 *   `startDate` as ISO `yyyy-MM-dd` (NZ spreadsheet forms normalised; omitted
 *   when blank).
 * - End-date column (override or reserved `End Date`) → per-row `endDate`
 *   as ISO `yyyy-MM-dd` (same normalisation; omitted when blank).
 * - `Row Mode` → per-row `rowMode` override (`Create` / `Update`,
 *   case-insensitive); otherwise the wizard-level `rowMode` (default `Create`).
 * - Reserved/informational columns (`Employee Name`, `Start Date`, `End Date`,
 *   `Row Mode`, the component and employee-code columns) plus any mapped date
 *   columns are dropped; every other column becomes a candidate parameter
 *   value in `columns`.
 *
 * Empty rows are skipped by the parser. Header/value whitespace is trimmed.
 */
export function buildImportRows(
  csvText: string,
  {
    companyCode,
    componentColumn,
    startDateColumn,
    endDateColumn,
    rowMode,
    componentResolutions,
  }: BuildImportRowsOptions,
): ImportRow[] {
  const preview = parseCsv(csvText);
  const headers = preview.headers;

  const employeeHeader = headers.find(
    (h) => h.toLowerCase() === EMPLOYEE_CODE_HEADER_LC,
  );
  const startDateHeader = resolveDateColumnHeader(
    headers,
    startDateColumn,
    START_DATE_HEADER_LC,
  );
  const endDateHeader = resolveDateColumnHeader(
    headers,
    endDateColumn,
    END_DATE_HEADER_LC,
  );
  const rowModeHeader = headers.find(
    (h) => h.toLowerCase() === ROW_MODE_HEADER_LC,
  );
  const componentHeader =
    (componentColumn && componentColumn.trim()) || preview.componentColumn || "";

  const defaultRowMode: RowMode = rowMode ?? RowMode.Create;

  return preview.rows.map((row) => {
    const columns: Record<string, string> = {};
    for (const header of headers) {
      if (RESERVED_LC.has(header.toLowerCase())) continue;
      if (header === componentHeader) continue;
      if (header === startDateHeader || header === endDateHeader) continue;
      columns[header] = row[header] ?? "";
    }

    const startDate = startDateHeader
      ? wireDateFromCsvCell(row[startDateHeader] ?? "", "Start date")
      : undefined;
    const endDate = endDateHeader
      ? wireDateFromCsvCell(row[endDateHeader] ?? "", "End date")
      : undefined;
    const perRowMode = rowModeHeader ? parseRowMode(row[rowModeHeader]) : null;

    const importRow: ImportRow = {
      companyCode,
      employeeCode: employeeHeader ? (row[employeeHeader] ?? "") : "",
      componentName: componentHeader ? (row[componentHeader] ?? "") : "",
      columns,
      rowMode: perRowMode ?? defaultRowMode,
    };
    if (startDate !== undefined) {
      importRow.startDate = startDate;
    }
    // Carry the disambiguating id for a resolved ambiguous name (D4). The name
    // is trimmed by the parser, matching the (trimmed) discovery keys the
    // resolution map is built from.
    const resolvedId = componentResolutions?.[importRow.componentName]
      ?.companyComponentId;
    if (resolvedId) {
      importRow.companyComponentId = resolvedId;
    }
    // Omit `endDate` entirely when blank so the wire row carries no empty value.
    if (endDate !== undefined) {
      importRow.endDate = endDate;
    }
    return importRow;
  });
}

/**
 * Find rows whose component name is ambiguous (`isAmbiguous`) but carries no
 * selected `companyComponentId`. These MUST NOT be posted — the server would
 * reject them as `PAYCOMP_COMPONENT_AMBIGUOUS` — so the commit surfaces them as
 * blocking validation errors instead (D4; never silently pick an instance).
 */
export function findUnresolvedAmbiguousRows(
  rows: ImportRow[],
  componentResolutions: ComponentResolutionMap = {},
): UnresolvedAmbiguousRow[] {
  const unresolved: UnresolvedAmbiguousRow[] = [];
  rows.forEach((row, index) => {
    const resolution = componentResolutions[row.componentName];
    if (resolution?.isAmbiguous && !row.companyComponentId) {
      unresolved.push({
        rowIndex: index + 1,
        employeeCode: row.employeeCode,
        componentName: row.componentName,
      });
    }
  });
  return unresolved;
}
